import { EXPERIENCE_STATES, MENU_ITEMS } from "constants/experiences"
import { formatMultiDayTerms } from "pagesV2/pro/Bookings/helpers/multiDayPricing"

export const formatDuration = (tripLength, availableHours) => {
  if (tripLength === "full_day") return "Full day"
  if (tripLength === "half_day") return "Half day"
  return availableHours && availableHours.length > 0
    ? `${availableHours[0].start} - ${availableHours[0].end}`
    : "Varies"
}

// The API may spell the unit in the plural ("minutes"), as the wizard's defaults allow for.
const isMinutesUnit = (unit) => unit === "minute" || unit === "minutes"

/**
 * A single-day length saved as duration_value + duration_unit ("minute" / "hour"), as the ticketed
 * wizard does, spelled out: "2 hours", "30 minutes". Null when no length is set.
 *
 * @param {Object} record - trip/experience record (or anything with duration_value / duration_unit)
 * @param {Function} [t] - i18n translation function (optional; falls back to English)
 */
export const formatDurationFields = (record, t) => {
  const translate = typeof t === "function" ? t : (_key, opts) => opts?.defaultValue
  const count = Number(record?.duration_value)
  if (!(count > 0)) return null
  if (isMinutesUnit(record?.duration_unit)) {
    return translate("experiences.terms.minutes", { count, defaultValue: `${count} ${count === 1 ? "minute" : "minutes"}` })
  }
  return translate("experiences.terms.hours", { count, defaultValue: `${count} ${count === 1 ? "hour" : "hours"}` })
}

// Ticketed trips come back without available_hours_range (the backend is to fill it from
// duration_value + duration_unit), so their label is built from those two fields. Both are needed:
// some responses (the embedded list) have duration_value without the unit.
const hasTicketedDuration = (record) =>
  record?.experience_type === "ticketed" && Number(record?.duration_value) > 0 && !!record?.duration_unit

/**
 * Display label for an experience's "terms" / duration in the card, listing
 * table and preview. Multi-day experiences show their day count (or "Multi-Day")
 * instead of an hours range, which only applies to single-day experiences.
 *
 * @param {Object} record - trip/experience record
 * @param {Function} [t] - i18n translation function (optional; falls back to English)
 */
export const formatExperienceTerms = (record, t) => {
  const translate = typeof t === "function" ? t : (_key, opts) => opts?.defaultValue
  if (record?.duration_type === "multi_day") {
    // A multi-day experience sells either a set of fixed durations ("2, 3, 4 days") or any length
    // inside a range ("2-10 days"); duration_value only describes the legacy single-length records.
    const terms = formatMultiDayTerms(record)
    if (terms) return terms
    return translate("experiences.terms.multi_day", { defaultValue: "Multi-Day" })
  }
  // Same short form as available_hours_range: "2h", "30m".
  if (!record?.available_hours_range && hasTicketedDuration(record)) {
    return `${Number(record.duration_value)}${isMinutesUnit(record.duration_unit) ? "m" : "h"}`
  }
  return record?.available_hours_range ?? "—"
}

/**
 * Same as formatExperienceTerms but spells out hours in full for single-day
 * experiences (e.g. "2h" -> "2 hours", "1h - 6h" -> "1 - 6 hours"). Multi-day
 * experiences keep their day count.
 *
 * @param {Object} record - trip/experience record
 * @param {Function} [t] - i18n translation function (optional; falls back to English)
 */
export const formatExperienceTermsFull = (record, t) => {
  const translate = typeof t === "function" ? t : (_key, opts) => opts?.defaultValue
  if (record?.duration_type === "multi_day") {
    return formatExperienceTerms(record, t)
  }
  // Read the fields directly: a minutes length ("30m") would be misread as hours below.
  if (hasTicketedDuration(record)) return formatDurationFields(record, t)
  const range = record?.available_hours_range
  if (!range) return "—"
  // A minutes length as the backend writes it for ticketed trips ("30m").
  const minutes = range.match(/^\s*(\d+)\s*m\s*$/)
  if (minutes) return formatDurationFields({ duration_value: minutes[1], duration_unit: "minute" }, t)
  const numbers = range.match(/\d+/g)
  if (!numbers) return range
  if (numbers.length >= 2) {
    const [min, max] = [numbers[0], numbers[numbers.length - 1]]
    return translate("experiences.terms.hours_range", { min, max, defaultValue: `${min} - ${max} hours` })
  }
  const count = Number(numbers[0])
  return translate("experiences.terms.hours", { count, defaultValue: `${count} ${count === 1 ? "hour" : "hours"}` })
}

const PRICE_SUFFIX_PRICING_TYPES = ["charge_flat_rate", "charge_per_person", "set_minimum_rate"]

/**
 * Suffix shown after the "from $X" price on listing cards and rows ("/Person", "/Flat", "/Min").
 * Ticketed listings are always priced per ticket, i.e. per person; private ones follow their
 * pricing type. Returns "" when there's no known pricing type.
 *
 * @param {Object} record - trip/experience record
 * @param {Function} t - i18n translation function
 */
// Places on one ticketed departure: the sum of the enabled ticket types' max_capacity (client, 8 Oct
// 2026). 0 while no ticket type has a capacity.
export const getDepartureCapacity = (record) =>
  (record?.ticket_types || [])
    .filter((ticketType) => ticketType?.enabled !== false)
    .reduce((sum, ticketType) => sum + (Number(ticketType?.max_capacity) || 0), 0)

// The "Up to N" / "N max" guest count: a ticketed trip's departure places, else max_guests. 0 when unknown.
export const getMaxGuests = (record) =>
  record?.experience_type === "ticketed" ? getDepartureCapacity(record) : Number(record?.max_guests) || 0

export const getPriceSuffix = (record, t) => {
  const pricingType = record?.experience_type === "ticketed" ? "charge_per_person" : record?.pricing_type
  return PRICE_SUFFIX_PRICING_TYPES.includes(pricingType) ? t(`experiences.price.suffix.${pricingType}`) : ""
}

export const formatTimeDuration = (minutes) => {
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hrs && mins) return `${hrs}h ${mins}m`;
  if (hrs) return `${hrs}h`;
  if (mins) return `${mins}m`;
  return "0m";
};

export const getActionConditions = (status) => {
  switch (status) {
    case EXPERIENCE_STATES.published:
      return {
        [MENU_ITEMS.EDIT_CATEGORY]: true,
        [MENU_ITEMS.SHARE]: true,
        [MENU_ITEMS.DUPLICATE]: true,
        [MENU_ITEMS.DEACTIVATE]: true,
        [MENU_ITEMS.DELETE]: true,
        [MENU_ITEMS.PUBLISH]: false
      }
    case EXPERIENCE_STATES.draft:
      return {
        [MENU_ITEMS.EDIT_CATEGORY]: false,
        [MENU_ITEMS.SHARE]: false,
        [MENU_ITEMS.DUPLICATE]: true,
        [MENU_ITEMS.DEACTIVATE]: false,
        [MENU_ITEMS.DELETE]: true,
        [MENU_ITEMS.PUBLISH]: false
      }
    case EXPERIENCE_STATES.deactivated:
      return {
        [MENU_ITEMS.EDIT_CATEGORY]: false,
        [MENU_ITEMS.SHARE]: false,
        [MENU_ITEMS.DUPLICATE]: true,
        [MENU_ITEMS.DEACTIVATE]: false,
        [MENU_ITEMS.DELETE]: true,
        [MENU_ITEMS.PUBLISH]: true
      }
    default:
      return {
         [MENU_ITEMS.EDIT_CATEGORY]: true,
         [MENU_ITEMS.PUBLISH]: true,
        [MENU_ITEMS.SHARE]: true,
        [MENU_ITEMS.DUPLICATE]: true,
        [MENU_ITEMS.DEACTIVATE]: true,
        [MENU_ITEMS.DELETE]: true,
      }
  }
}

export const getDropdownItems = (menuConfig, conditions) => {
  return Object.values(menuConfig)
    .map(item => ({
      ...item,
      icon: <item.icon />,
      condition: conditions[item.key]
    }))
    .filter(item => item.condition)
} 
